import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Camera, Plus, Trash, CircleNotch, FilePdf } from "@phosphor-icons/react";

async function processPage(file) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale);
  c.height = Math.round(img.height * scale);
  const ctx = c.getContext("2d");
  ctx.filter = "contrast(1.18) brightness(1.06) saturate(0.85)";
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
  const lum = (x, y) => { const i = (y * width + x) * 4; return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]; };
  const corners = [lum(2, 2), lum(width - 3, 2), lum(2, height - 3), lum(width - 3, height - 3)];
  const bg = corners.reduce((a, b) => a + b, 0) / 4;
  const rowDiffers = (y) => { let n = 0; for (let x = 0; x < width; x += 6) if (Math.abs(lum(x, y) - bg) > 34) n++; return n > width / 60; };
  const colDiffers = (x) => { let n = 0; for (let y = 0; y < height; y += 6) if (Math.abs(lum(x, y) - bg) > 34) n++; return n > height / 60; };
  let top = 0, bottom = height - 1, left = 0, right = width - 1;
  while (top < height * 0.35 && !rowDiffers(top)) top += 2;
  while (bottom > height * 0.65 && !rowDiffers(bottom)) bottom -= 2;
  while (left < width * 0.35 && !colDiffers(left)) left += 2;
  while (right > width * 0.65 && !colDiffers(right)) right -= 2;
  const pad = Math.round(Math.min(width, height) * 0.015);
  top = Math.max(0, top - pad); left = Math.max(0, left - pad);
  bottom = Math.min(height - 1, bottom + pad); right = Math.min(width - 1, right + pad);
  const out = document.createElement("canvas");
  out.width = right - left + 1;
  out.height = bottom - top + 1;
  out.getContext("2d").drawImage(c, left, top, out.width, out.height, 0, 0, out.width, out.height);
  URL.revokeObjectURL(img.src);
  return { dataUrl: out.toDataURL("image/jpeg", 0.82), w: out.width, h: out.height };
}

export function SnapDocument({ open, onOpenChange, engagementId }) {
  const qc = useQueryClient();
  const [pages, setPages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const camRef = useRef();

  const addPages = async (files) => {
    setProcessing(true);
    try {
      for (const f of files) {
        const p = await processPage(f);
        setPages((prev) => [...prev, p]);
      }
      toast.success(`${files.length} page${files.length > 1 ? "s" : ""} captured & auto-cropped`);
    } catch {
      toast.error("Couldn't process that image — try again");
    }
    setProcessing(false);
  };

  const upload = useMutation({
    mutationFn: async () => {
      const { jsPDF } = await import("jspdf");
      const pdf = new jsPDF({ unit: "pt", format: "letter" });
      pages.forEach((p, i) => {
        if (i > 0) pdf.addPage();
        const pw = pdf.internal.pageSize.getWidth() - 40, ph = pdf.internal.pageSize.getHeight() - 40;
        const r = Math.min(pw / p.w, ph / p.h);
        pdf.addImage(p.dataUrl, "JPEG", 20, 20, p.w * r, p.h * r);
      });
      const blob = pdf.output("blob");
      const fd = new FormData();
      fd.append("file", new File([blob], `snapped_document_${Date.now()}.pdf`, { type: "application/pdf" }));
      fd.append("engagement_id", engagementId);
      return api.post("/documents/upload", fd);
    },
    onSuccess: () => {
      toast.success(`Scanned ${pages.length}-page PDF uploaded — AI is filing it`);
      setPages([]);
      onOpenChange(false);
      ["portal", "gaps"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setPages([]); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="snap-document-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-sm flex items-center gap-1.5"><Camera size={16} /> Snap a document</DialogTitle>
          <DialogDescription className="text-xs">Photograph each page — we auto-crop and clean it into a single scan-quality PDF for our AI to read.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2" data-testid="snap-pages">
          {pages.map((p, i) => (
            <div key={i} className="relative rounded-md border border-zinc-200 overflow-hidden group" data-testid={`snap-page-${i}`}>
              <img src={p.dataUrl} alt={`Page ${i + 1}`} className="w-full h-24 object-cover" />
              <span className="absolute bottom-1 left-1 text-[9px] bg-zinc-900/80 text-white rounded-sm px-1">p.{i + 1}</span>
              <button data-testid={`snap-remove-${i}`} onClick={() => setPages(pages.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-rose-600"><Trash size={11} /></button>
            </div>
          ))}
          <button data-testid="snap-add-page-btn" disabled={processing} onClick={() => camRef.current?.click()}
            className="rounded-md border border-dashed border-zinc-300 h-24 flex flex-col items-center justify-center gap-1 text-zinc-500 hover:border-zinc-500 hover:text-zinc-900 transition-colors duration-150">
            {processing ? <CircleNotch size={18} className="animate-spin" /> : pages.length ? <Plus size={18} /> : <Camera size={18} />}
            <span className="text-[10px]">{pages.length ? "Add page" : "Snap page 1"}</span>
          </button>
        </div>
        <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="snap-camera-input"
          onChange={(e) => { if (e.target.files?.length) addPages([...e.target.files]); e.target.value = ""; }} />
        <Button data-testid="snap-upload-btn" className="w-full" disabled={!pages.length || upload.isPending || !engagementId} onClick={() => upload.mutate()}>
          {upload.isPending ? <CircleNotch size={14} className="animate-spin mr-1" /> : <FilePdf size={14} className="mr-1" />}
          {upload.isPending ? "Building PDF & uploading…" : `Upload ${pages.length || ""} page${pages.length === 1 ? "" : "s"} as one PDF`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
